import { Range } from "./types";

/**
 * Decodes a cell reference string to a row/column pair.
 * Examples: "A1" -> {r: 0, c: 0}, "Z10" -> {r: 9, c: 25}, "AA1" -> {r: 0, c: 26}
 */
function decodeCell(ref: string): { r: number; c: number } {
  let col = 0;
  let row = 0;
  let i = 0;

  // Parse column letters (A-Z, AA-ZZ, etc.)
  while (i < ref.length && /[A-Z]/i.test(ref[i])) {
    col = col * 26 + (ref.charCodeAt(i) & 0x1f);
    i++;
  }
  col--; // Convert from 1-indexed to 0-indexed

  // Parse row number
  row = parseInt(ref.slice(i), 10) - 1; // Convert from 1-indexed to 0-indexed

  return { r: row, c: col };
}

/**
 * Decodes a range string (e.g., "A1:D10") to start and end row/column pairs.
 * @param ref - The range reference string (e.g., "A1:D10")
 * @returns Object with start (s) and end (e) positions, each containing row (r) and column (c)
 */
export function decodeRange(ref: string): Range {
  const parts = ref.split(":");
  const start = decodeCell(parts[0]);

  if (parts.length === 1) {
    // Single cell reference
    return { s: start, e: start };
  }

  const end = decodeCell(parts[1]);
  return { s: start, e: end };
}

/**
 * Encodes a row/column pair to a cell reference string.
 * @param cell - Object with row (r) and column (c), both 0-indexed
 * @returns Cell reference string (e.g., "A1", "AA100")
 */
export function encodeCell(cell: { r: number; c: number }): string {
  let col = cell.c;
  let colStr = "";

  // Convert column number to letters (A-Z, AA-ZZ, etc.)
  // Column is 0-indexed, so we add 1 before conversion
  col++;
  while (col > 0) {
    col--;
    colStr = String.fromCharCode(65 + (col % 26)) + colStr;
    col = Math.floor(col / 26);
  }

  // Row is 0-indexed, so we add 1 for display
  return colStr + (cell.r + 1);
}
